import React from 'react';
import { Box, Button, Flex, Modal, ModalBody, ModalContent, ModalHeader, ModalOverlay, Text } from '@chakra-ui/core';
import { FiBriefcase } from 'react-icons/fi';
import { IoBusinessOutline } from 'react-icons/io5';
import { useTranslation } from 'react-i18next';
import colors from '../../../styles/colors';

export type SeekerExistsInfoModalProps = {
  isOpen: boolean;
  onClose: () => void;
  heading: string;
  subheading: string;
  onFindJob: () => void;
  onCreateRecruiterAccount: () => void;
};

type OptionCardProps = {
  title: string;
  buttonLabel: string;
  description: string;
  borderColor: string;
  backgroundColor: string;
  iconBackgroundColor: string;
  iconColor: string;
  buttonBackgroundColor: string;
  icon: React.ReactNode;
  onClick: () => void;
  buttonTestId: string;
};

const OptionCard: React.FC<OptionCardProps> = ({
  title,
  buttonLabel,
  description,
  borderColor,
  backgroundColor,
  iconBackgroundColor,
  iconColor,
  icon,
  buttonBackgroundColor,
  onClick,
  buttonTestId,
}) => {
  return (
    <Box
      width="100%"
      height="100%"
      display="flex"
      flexDirection="column"
      borderWidth="1px"
      borderColor={borderColor}
      bg={backgroundColor}
      borderRadius="md"
      px={4}
      py={7}
      textAlign="center"
    >
      <Flex justify="center" mb={4}>
        <Flex align="center" justify="center" w="48px" h="48px" borderRadius="full" bg={iconBackgroundColor} color={iconColor}>
          {icon}
        </Flex>
      </Flex>

      <Text fontWeight="700" fontSize="md" mb={4}>
        {title}
      </Text>

      <Button
        outline="none"
        width="100%"
        borderRadius="8px"
        height="40px"
        lineHeight="1"
        fontWeight={400}
        color={colors.white}
        bg={buttonBackgroundColor}
        onClick={onClick}
        data-testid={buttonTestId}
      >
        {buttonLabel}
      </Button>

      <Text mt={4} fontSize="sm" color="gray.600">
        {description}
      </Text>
    </Box>
  );
};

export const SeekerExistsInfoModal: React.FC<SeekerExistsInfoModalProps> = ({
  isOpen,
  onClose,
  heading,
  subheading,
  onFindJob,
  onCreateRecruiterAccount,
}) => {
  const { t } = useTranslation('login');
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      isCentered
      size="xl"
      closeOnEsc={false}
      closeOnOverlayClick={false}
      autoFocus={false}
    >
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent borderRadius="lg" overflow="hidden">
        <ModalHeader data-testid="SeekerExistsMessage" pt={7} px={8} pb={3} fontSize="24px" fontWeight="700" textAlign="center">
          {heading}
        </ModalHeader>

        <ModalBody px={8} pb={8}>
          <Text fontSize="sm" color="gray.600" mb={7} textAlign="center">
            {subheading}
          </Text>

          <Box display="grid" gridTemplateColumns="repeat(2, minmax(0, 1fr))" style={{ columnGap: '24px' }}>
            <OptionCard
              title={t('seeker_modal_job_search_portal_title')}
              buttonLabel={t('seeker_modal_find_a_job_button')}
              description={t('seeker_modal_job_search_portal_description')}
              borderColor="rgba(255, 102, 0, 0.2)"
              backgroundColor={colors.seashell}
              iconBackgroundColor="rgba(255, 102, 0, 0.06)"
              iconColor={colors.orange[500]}
              onClick={onFindJob}
              buttonBackgroundColor={colors.orange[500]}
              icon={
                <Box as="span" aria-hidden fontSize="18px" lineHeight="1">
                  <FiBriefcase fontSize="28px" />
                </Box>
              }
              buttonTestId="FindJobButton"
            />

            <OptionCard
              title={t('seeker_modal_recruiter_platform_title')}
              buttonLabel={t('seeker_modal_create_recruiter_account_button')}
              description={t('seeker_modal_recruiter_platform_description')}
              borderColor="rgba(31, 60, 186, 0.27)"
              backgroundColor={colors.alpineWhite}
              iconBackgroundColor="rgba(31, 60, 186, 0.12)"
              iconColor={colors.blue.default}
              onClick={onCreateRecruiterAccount}
              buttonBackgroundColor={colors.blue.default}
              icon={
                <Box as="span" aria-hidden fontSize="18px" lineHeight="1">
                  <IoBusinessOutline fontSize="26px" />
                </Box>
              }
              buttonTestId="CreateRecruiterAccountButton"
            />
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
